import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import {
  SKIP_GLOBAL_HTTP_ERROR_UI,
  SKIP_UNAUTHORIZED_SESSION_HANDLING,
} from '@core/services/http/http-context.tokens';
import { resolveHttpErrorMessage } from '@core/services/http/http-error-message.util';
import { ToastService } from '@core/services/toast/toast.service';
import { SessionService } from '../services/authentication/session.service';
import { AuthNavigationService } from '../services/authentication/auth-navigation.service';
import { AuthSessionStore } from '../state/auth-session.store';

export {
  SKIP_GLOBAL_HTTP_ERROR_UI,
  SKIP_UNAUTHORIZED_SESSION_HANDLING,
} from '@core/services/http/http-context.tokens';

/**
 * Catches HTTP errors, optionally shows a deduped global toast, and re-throws.
 * GET failures are owned by feature stores (inline error UI); mutations toast here
 * unless {@link SKIP_GLOBAL_HTTP_ERROR_UI} is set on the request.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const sessionService = inject(SessionService);
  const authNavigation = inject(AuthNavigationService);
  const authSession = inject(AuthSessionStore);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const skipUnauthorizedCleanup = req.context.get(
          SKIP_UNAUTHORIZED_SESSION_HANDLING,
        );
        const claimedUnauthorizedCleanup =
          error.status === 401 &&
          !skipUnauthorizedCleanup &&
          !sessionService.shouldDeferUnauthorizedSessionHandling() &&
          sessionService.tryClaimUnauthorizedHttpCleanup();

        if (claimedUnauthorizedCleanup) {
          authSession.resolveAccess('unauthenticated', {
            source: 'http',
            path: req.url,
          });
          void sessionService.handleUnauthorizedSession().finally(() => {
            sessionService.releaseUnauthorizedHttpCleanupClaim();
          });
        }

        if (error.status === 403) {
          authSession.resolveAccess('forbidden', {
            source: 'http',
            path: req.url,
          });
        }
        if (shouldNavigateForHttpError(req, error.status)) {
          void authNavigation.navigateForStatus(error.status);
        }

        if (
          shouldShowErrorNotification(
            error,
            {
              skipUnauthorizedCleanup,
              claimedUnauthorizedCleanup,
              sessionService,
            },
            req,
          )
        ) {
          toast.error(resolveHttpErrorMessage(error));
        }
      }
      return throwError(() => error);
    }),
  );
};

function shouldNavigateForHttpError(
  req: Parameters<HttpInterceptorFn>[0],
  status: number,
): boolean {
  if (req.context.get(SKIP_GLOBAL_HTTP_ERROR_UI)) {
    return false;
  }

  return status === 403 || status === 404 || status >= 500;
}

function shouldShowErrorNotification(
  error: HttpErrorResponse,
  context: {
    skipUnauthorizedCleanup: boolean;
    claimedUnauthorizedCleanup: boolean;
    sessionService: SessionService;
  },
  req: Parameters<HttpInterceptorFn>[0],
): boolean {
  if (req.context.get(SKIP_GLOBAL_HTTP_ERROR_UI)) {
    return false;
  }

  if (req.method === 'GET' && error.status !== 401) {
    return false;
  }

  if (error.status !== 401) {
    return true;
  }

  if (context.skipUnauthorizedCleanup) {
    return false;
  }

  if (context.sessionService.shouldDeferUnauthorizedSessionHandling()) {
    return false;
  }

  if (context.claimedUnauthorizedCleanup) {
    return true;
  }

  return !context.sessionService.isUnauthorizedSessionCleanupInFlight();
}
